import { useContext, useMemo } from 'react';

import { useUserColorMode } from '@bonusly/design-system';

import { getSeasonConfig } from './get-season-config';
import { SeasonalThemeContext } from './seasonal-theme-context';

/**
 * Hook to get the appropriate logo based on seasonal theme
 * Returns the seasonal logo when theme is enabled, undefined otherwise
 * Uses logoDark / logoLight from the active season config based on resolved color mode
 *
 * Note: If used outside of SeasonalThemeProvider, returns undefined (graceful degradation)
 */
export const useSeasonalLogo = () => {
	const context = useContext(SeasonalThemeContext);
	const seasonConfig = getSeasonConfig();
	const { isDarkMode } = useUserColorMode();

	// Gracefully handle usage outside of provider
	const isSeasonalThemeEnabled = context?.isSeasonalThemeEnabled ?? false;

	// Memoize the return value to prevent unnecessary re-renders
	return useMemo(
		() => ({
			logoUrl: isSeasonalThemeEnabled ? (isDarkMode ? seasonConfig.logoDark : seasonConfig.logoLight) : undefined,
			isSeasonalTheme: isSeasonalThemeEnabled,
		}),
		[isSeasonalThemeEnabled, isDarkMode, seasonConfig.logoDark, seasonConfig.logoLight]
	);
};
